// libraries
import type { DealHistory, StateTree } from '@makemydeal/dr-dash-types';
import { registerContextResolver } from '@makemydeal/dr-dash-components';
import * as compositeSelectors from '@makemydeal/dr-dash-store/selectors/compositeSelectors';
import * as dealEffectiveSelectors from '@makemydeal/dr-dash-store/selectors/dealEffectiveSelectors';
import * as calculationDetailsSelectors from '@makemydeal/dr-dash-store/selectors/calculationDetailsSelectors';
import * as dueAtSigningSelectors from '@makemydeal/dr-dash-store/selectors/dueAtSigningSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import * as tradeInsSelectors from '@makemydeal/dr-dash-store/selectors/tradeIns';
import { getInFlightPaymentCount } from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

/** Name the `AmountFinanced` read tags itself with. Contract surface — keep in sync. */
export const FINANCE_BREAKDOWN_CONTEXT = 'financeBreakdown';

/**
 * Companion values for the finance breakdown identity (US2058402):
 *
 *     Net Selling Price - Net Trades - Rebates - Cash Down == Amt. Financed
 *
 * WHY THIS IS NEEDED AT ALL
 * `SelectorRead` is change-only, so the five terms land on DIFFERENT events.
 * Editing Cash Down emitted `effectiveDownPayment` at actionIndex 172 and
 * `effectiveAmountFinanced` at 188 — once the payment service answered — while
 * `netSellingPrice`, the trade total and the rebate total never re-emitted
 * because they had not changed. Checking the identity at the moment
 * `amtFinanced` was read therefore requires carrying three values forward from
 * earlier events, and NRQL has no window functions and no self-join. Measured
 * consequence: the check was only exact on page load, where all five happen to
 * emit together.
 *
 * Attaching the inputs to the `amtFinanced` read makes it exact per read. They
 * are resolved from the same state snapshot the selector just read, so they
 * cannot be stale relative to each other.
 *
 * EACH SELECTOR MIRRORS ITS CARD CALL SITE EXACTLY. Getting any of these subtly
 * wrong would manufacture a variance that the UI never showed, which is worse
 * than no telemetry:
 *
 *  - `netSellingPrice`  NetSellingPrice.interstate.tsx reads it through
 *    `createDealRootSelector`, which substitutes the deal for the state when a
 *    deal is present -> `getNetSellingPrice(deal ?? state)`.
 *  - `netTrade`         NetTrade.interstate.tsx reads it with a PLAIN tracked
 *    selector, i.e. always from live state, never deal-scoped. Mirrored as-is
 *    rather than "corrected" here, so the companion always matches what the
 *    card rendered.
 *  - `rebates`          Rebates.interstate.tsx picks between two selectors on
 *    `isProgramIncentivesRestorationEnabled`, reading the TOGGLE from live
 *    state but the VALUE deal-scoped. Both halves mirrored.
 *  - `cashDown`         CashDown.interstate.tsx uses the deal-effective form,
 *    which takes the deal as a second argument rather than substituting it.
 *
 * `arg` is the `deal` handed through from `TrackedSelectorOptions.contextArg`.
 * It is `undefined` for the primary offer and a `DealHistory` for an
 * offer-compare column; without honouring it, a history column would attach the
 * primary offer's numbers to a historical deal's amount financed.
 *
 * `identityVariance` (US2119069) collapses the whole identity into one signed
 * scalar so the dwell stream can carry the verdict without repeating five legs.
 * It uses THIS resolver's own `amountFinanced`, read from the same snapshot as
 * the four inputs, whereas the dashboard's arithmetic subtracts the `ctx.*` legs
 * from the event's `value`, which react-redux resolved from the RENDER snapshot.
 * The two agreeing means the read was coherent; the two disagreeing surfaces a
 * render-snapshot tear that dashboard arithmetic alone could never show.
 */
export const registerDashBreakdownContextResolver = (): void => {
    registerContextResolver(FINANCE_BREAKDOWN_CONTEXT, (state, arg) => {
        const stateTree = state as StateTree;
        const deal = arg as DealHistory | undefined;

        // `createDealRootSelector` semantics: the deal stands in for the state.
        const dealScope = (deal as unknown as StateTree) ?? stateTree;

        const isRestorationEnabled = featureToggleSelectors.isProgramIncentivesRestorationEnabled(stateTree);
        const rebateSelector = isRestorationEnabled
            ? offerReduxSelectors.getAppliedIncentivesTotalForDisplay
            : offerReduxSelectors.getAppliedIncentivesTotalForNonDealerCash;

        const netSellingPrice = compositeSelectors.getNetSellingPrice(dealScope);
        const netTrade = tradeInsSelectors.getTotalNetTradeValueForIncludedActiveTradeIns(stateTree);
        const rebates = rebateSelector(dealScope);
        const cashDown = dealEffectiveSelectors.selectEffectiveDownPayment(stateTree, deal);
        const amountFinanced = dealEffectiveSelectors.selectEffectiveAmountFinanced(stateTree, deal);

        return {
            netSellingPrice,
            netTrade,
            rebates,
            cashDown,
            identityVariance:
                Number(netSellingPrice) - Number(netTrade) - Number(rebates) - Number(cashDown) - Number(amountFinanced),
            inFlightPaymentCount: getInFlightPaymentCount(stateTree),
            amountFinancedAtResolve: amountFinanced
        };
    });
};

/** Name the lease `DueAtSigning` read tags itself with. Contract surface — keep in sync. */
export const LEASE_BREAKDOWN_CONTEXT = 'leaseBreakdown';

/**
 * Companion values for the lease breakdown identity (US2123622):
 *
 *     1st Mo. Payment + Upfront Taxes + Lender Fees + Government Fees + Dealer Fees +
 *     Protection Products + Accessories + Security Deposit + Cap Cost Reduction == Due at Signing
 *
 * The first term is CONDITIONAL: the card renders the 1st Monthly Payment row
 * only when `!firstPaymentCapitalized`, so a capitalized-first-payment deal
 * satisfies the identity WITHOUT it. `firstPaymentCapitalized` therefore ships
 * as a companion too — without it every such deal reads as a false variance of
 * exactly one monthly payment, which is the failure mode this bag exists to
 * prevent.
 */
export const registerDashLeaseBreakdownContextResolver = (): void => {
    registerContextResolver(LEASE_BREAKDOWN_CONTEXT, (state, arg) => {
        const stateTree = state as StateTree;
        const deal = arg as DealHistory | undefined;

        if (!featureToggleSelectors.isLeaseReduxTelemetryEnabled(stateTree)) return null;

        // `createDealRootSelector` semantics: the deal stands in for the state.
        const dealScope = (deal as unknown as StateTree) ?? stateTree;
        const isFirstPaymentCapitalized =
            offerReduxSelectors.getFirstPaymentCapitalized(stateTree) ??
            calculationDetailsSelectors.getIsFirstPaymentCapitalized(stateTree);
        const isOnePayLease = offerReduxSelectors.isOnePayLease(stateTree);
        const isOnePayLeaseFirstPaymentEnabled = featureToggleSelectors.isOnePayLeaseFirstPaymentEnabled(stateTree);

        let firstMonthlyPayment: number;
        if (isFirstPaymentCapitalized) {
            firstMonthlyPayment = 0;
        } else if (isOnePayLeaseFirstPaymentEnabled && isOnePayLease) {
            firstMonthlyPayment =
                offerReduxSelectors.getFirstPaymentAmount(dealScope) ?? offerReduxSelectors.getBaseMonthlyPaymentTotal(dealScope);
        } else {
            firstMonthlyPayment = offerSelectors.getMonthlyPayment(dealScope);
        }

        return {
            firstMonthlyPayment,
            upfrontTaxes: offerReduxSelectors.getUpfrontTaxTotal(stateTree),
            lenderFees: offerReduxSelectors.getDiaLenderFees(stateTree),
            governmentFees: offerReduxSelectors.getDiaGovFees(dealScope),
            dealerFees: offerReduxSelectors.getDiaDealerFees(dealScope),
            protectionProducts: offerReduxSelectors.getDiaProtectionProducts(dealScope),
            accessories: offerReduxSelectors.getDiaAccessories(dealScope),
            securityDeposit: offerSelectors.getSecurityDepositOverrideWithFallback(dealScope),
            capCostReduction: dueAtSigningSelectors.getDasCapCostReduction(dealScope),
            inFlightPaymentCount: getInFlightPaymentCount(stateTree),
            adjCapCostAtResolve: offerReduxSelectors.getAdjustedCapCost(dealScope),
            baseMonthlyPaymentsTotal: isOnePayLease ? offerReduxSelectors.getBaseMonthlyPaymentTotal(dealScope) : 0
        };
    });
};

/** Name the cash `TotalAmountDue` read tags itself with. Contract surface — keep in sync. */
export const CASH_BREAKDOWN_CONTEXT = 'cashBreakdown';

/**
 * Companion values for the cash breakdown identity (US2123622):
 *
 *     Net Selling Price - Net Trades - Rebates - Cash Down == Total Amount Due
 *
 * Gated behind its own toggle (`US2140882_Enable_Cash_Redux_Telemetry`, via
 * `isCashReduxTelemetryEnabled`), independent of the lease/finance telemetry
 * toggles.
 *
 * Mirrors the SAME card call sites `registerDashBreakdownContextResolver`
 * (finance) already mirrors, since Cash renders the identical
 * NetSellingPrice.interstate.tsx / NetTrade.interstate.tsx /
 * Rebates.interstate.tsx cards, plus CashDown.interstate.tsx. `cashDown` is 0
 * unless `enableCashDownPaymentField` is on, because only then does the Payment
 * Service net it (as `holdDeposit`) out of Total Amount Due. No amount-financed
 * leg — the cash identity has no financed term to close against.
 *
 * `TotalAmountDue.interstate.tsx` is shared by the main cash panel AND the
 * cash/finance offer-compare columns, so it attaches this context only when
 * its OWN deal-scoped `getCurrentOfferType` read resolves to `CASH` — not
 * based on which component rendered it. A cash compare-column read
 * (`CompareCashColumnDetail.interstate.tsx`) is therefore tagged exactly
 * like the main panel, resolved against that deal's own scope via
 * `contextArg`; a finance compare-column read
 * (`CompareFinanceColumnDetail.interstate.tsx`) never is, since its offer
 * type reads `FINANCE`, not `CASH`.
 */
export const registerDashCashBreakdownContextResolver = (): void => {
    registerContextResolver(CASH_BREAKDOWN_CONTEXT, (state, arg) => {
        const stateTree = state as StateTree;
        const deal = arg as DealHistory | undefined;

        if (!featureToggleSelectors.isCashReduxTelemetryEnabled(stateTree)) return null;

        // `createDealRootSelector` semantics: the deal stands in for the state.
        const dealScope = (deal as unknown as StateTree) ?? stateTree;

        const isRestorationEnabled = featureToggleSelectors.isProgramIncentivesRestorationEnabled(stateTree);
        const rebateSelector = isRestorationEnabled
            ? offerReduxSelectors.getAppliedIncentivesTotalForDisplay
            : offerReduxSelectors.getAppliedIncentivesTotalForNonDealerCash;

        const netSellingPrice = compositeSelectors.getNetSellingPrice(dealScope);
        const netTrade = tradeInsSelectors.getTotalNetTradeValueForIncludedActiveTradeIns(stateTree);
        const rebates = rebateSelector(dealScope);
        const cashDown = featureToggleSelectors.isCashDownPaymentFieldEnabled(stateTree)
            ? dealEffectiveSelectors.selectEffectiveDownPayment(stateTree, deal)
            : 0;
        const totalAmountDue = offerReduxSelectors.getCashTotalAmountDueAfterCredits(dealScope);

        return {
            netSellingPrice,
            netTrade,
            rebates,
            cashDown,
            identityVariance:
                Number(netSellingPrice) - Number(netTrade) - Number(rebates) - Number(cashDown) - Number(totalAmountDue),
            inFlightPaymentCount: getInFlightPaymentCount(stateTree),
            totalAmountDueAtResolve: totalAmountDue
        };
    });
};
